<!--
 * Path: packages/frontend/src/core/components/common/EntitySelect.vue
 * Task: Reusable, bulletproof wrapper component for Vuetify selects.
 * Update: Rebuilt to include sticky search, "Select All" functionality, checkboxes, and colored chips.
 * Fix: Changed chip representation to render a pure white icon without the EntityIcon avatar wrapper.
-->
<template>
  <div class="entity-select-wrapper">
    <v-select
      v-model="internalValue"
      :items="filteredItems"
      :item-title="computedItemTitle"
      :item-value="computedItemValue"
      :label="props.label"
      :placeholder="props.placeholder"
      :multiple="props.multiple"
      :disabled="props.disabled"
      :clearable="props.clearable"
      :chips="props.chips"
      :closable-chips="props.closableChips"
      variant="outlined"
      density="comfortable"
      hide-details="auto"
      class="entity-select"
      @update:menu="onMenuUpdate"
      v-bind="$attrs"
    >
      <template v-slot:prepend-item v-if="props.multiple">
        <!-- Sticky Search Bar -->
        <div class="sticky-search px-3 pt-2 pb-2 bg-surface">
          <v-text-field
            ref="filterInputRef"
            v-model="searchQuery"
            placeholder="Filter entries..."
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            @click.stop
            @keydown.stop
          ></v-text-field>
        </div>
        <!-- Select All / Deselect All Button -->
        <v-list-item @click="toggleSelectAll" class="mt-1" density="compact">
          <template v-slot:prepend>
            <div class="d-flex align-center justify-center me-3" style="width: 24px;">
              <v-icon size="small" :color="isAllSelected ? 'primary' : isSomeSelected ? 'primary' : 'medium-emphasis'">
                {{ isAllSelected ? 'mdi-checkbox-marked' : isSomeSelected ? 'mdi-minus-box' : 'mdi-checkbox-blank-outline' }}
              </v-icon>
            </div>
          </template>
          <v-list-item-title class="font-weight-bold">
            {{ isAllSelected ? 'Uncheck All' : 'Select All' }}
          </v-list-item-title>
        </v-list-item>
        <v-divider class="mb-1"></v-divider>
      </template>

      <template v-slot:item="{ props: itemProps, item }">
        <v-list-item v-bind="itemProps" :title="null" density="compact">
          <template v-slot:prepend>
            <!-- Checkbox (Only if multiple) -->
            <div v-if="props.multiple" class="d-flex align-center justify-center me-3" style="width: 24px;">
              <v-icon size="small" :color="isItemSelected(item) ? 'primary' : 'medium-emphasis'">
                {{ isItemSelected(item) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
              </v-icon>
            </div>
            <!-- Universally resolved EntityIcon -->
            <EntityIcon
              v-if="hasIconData(item)"
              :entity="item.raw"
              size="24"
              class="me-3"
            />
          </template>
          <v-list-item-title class="font-weight-medium">
            {{ getItemTitle(item) }}
          </v-list-item-title>
        </v-list-item>
      </template>

      <template v-if="props.chips" v-slot:chip="{ props: chipProps, item }">
        <v-chip
          v-bind="chipProps"
          :style="{ backgroundColor: getChipColor(item) }"
          variant="flat"
          size="small"
          class="text-white font-weight-bold mt-1 mb-1"
          @click:close="removeItem(item)"
        >
          <template v-slot:prepend>
            <v-icon
              v-if="getChipIcon(item)"
              color="white"
              size="14"
              class="me-1"
              style="margin-left: -4px;"
            >
              {{ getChipIcon(item) }}
            </v-icon>
          </template>
          {{ getItemTitle(item) }}
        </v-chip>
      </template>

      <template v-else-if="!props.multiple" v-slot:selection="{ item }">
        <div class="d-flex align-center">
          <EntityIcon
            v-if="hasIconData(item)"
            :entity="item.raw"
            size="20"
            class="me-2"
          />
          <span class="font-weight-medium text-high-emphasis text-truncate">
            {{ getItemTitle(item) }}
          </span>
        </div>
      </template>

      <template v-slot:no-data>
        <v-list-item>
          <v-list-item-title class="text-medium-emphasis text-body-2">
            {{ reactiveItems.length === 0 ? 'No options available' : 'No matching results found' }}
          </v-list-item-title>
        </v-list-item>
      </template>
    </v-select>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import EntityIcon from '@/core/components/common/EntityIcon.vue';
import { useAppStyle } from '@/core/composables/useAppStyle';

interface Props {
  modelValue?: any;
  items?: any[];
  itemTitle?: string;
  itemValue?: string;
  label?: string;
  placeholder?: string;
  multiple?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  chips?: boolean;
  closableChips?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  itemTitle: 'title',
  itemValue: 'id',
  multiple: false,
  disabled: false,
  clearable: false,
  chips: false,
  closableChips: false
});

const emit = defineEmits(['update:modelValue']);
const { resolveHexColor } = useAppStyle();

const searchQuery = ref<string>('');
const filterInputRef = ref<any>(null);

const internalValue = computed({
  get: () => {
    if (props.multiple) {
      return Array.isArray(props.modelValue) ? props.modelValue : (props.modelValue ? [props.modelValue] : []);
    }
    return props.modelValue;
  },
  set: (val) => emit('update:modelValue', val)
});

const reactiveItems = computed(() => [...(props.items || [])]);

const computedItemValue = computed(() => {
  if (props.itemValue !== 'id') return props.itemValue;
  if (reactiveItems.value && reactiveItems.value.length > 0) {
    const first = reactiveItems.value[0];
    if (first && typeof first === 'object') {
      if ('id' in first) return 'id';
      if ('key' in first) return 'key';
      if ('value' in first) return 'value';
    }
  }
  return 'id';
});

const computedItemTitle = computed(() => {
  if (props.itemTitle !== 'title') return props.itemTitle;
  if (reactiveItems.value && reactiveItems.value.length > 0) {
    const first = reactiveItems.value[0];
    if (first && typeof first === 'object') {
      if ('title' in first) return 'title';
      if ('name' in first) return 'name';
      if ('label' in first) return 'label';
    }
  }
  return 'title';
});

const filteredItems = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return reactiveItems.value;

  return reactiveItems.value.filter((item: any) => {
    const title = getItemTitle(item).toLowerCase();
    const isSelected = isItemSelected({ raw: item }); // Keep selected items visible even if they don't match the query
    return title.includes(query) || isSelected;
  });
});

const hasIconData = (item: any): boolean => {
  return !!(item?.raw && typeof item.raw === 'object' && (item.raw.entityType || item.raw.icon || item.raw.color));
};

const getItemTitle = (item: any): string => {
  if (!item) return 'Unknown Item';
  if (item.raw && typeof item.raw === 'object') {
    return item.raw[computedItemTitle.value] || item.raw.title || item.raw.name || item.raw.id || item.raw.key || 'Unknown Item';
  }
  return item.title || item.value || String(item);
};

const getChipColor = (item: any): string => {
  if(item?.raw?.color) return resolveHexColor(item.raw.color);
  return 'var(--v-theme-primary)';
};

const getChipIcon = (item: any): string | null => {
  if (item?.raw?.icon) return item.raw.icon;
  if (item?.raw?.symbol) return item.raw.symbol;
  return null;
}

const isItemSelected = (item: any): boolean => {
  if (!internalValue.value) return false;
  const targetId = item.raw ? item.raw[computedItemValue.value] : item[computedItemValue.value];
  if (targetId === undefined || targetId === null) return false;

  if (props.multiple && Array.isArray(internalValue.value)) {
    return internalValue.value.some((val: any) => {
      const valId = typeof val === 'object' ? val[computedItemValue.value] : val;
      return String(valId).toLowerCase() === String(targetId).toLowerCase();
    });
  }

  const valId = typeof internalValue.value === 'object' ? internalValue.value[computedItemValue.value] : internalValue.value;
  return String(valId).toLowerCase() === String(targetId).toLowerCase();
};

const removeItem = (item: any) => {
  if(!props.multiple || !Array.isArray(internalValue.value)) return;
  const valToRemove = item.raw ? item.raw[computedItemValue.value] : item[computedItemValue.value];

  internalValue.value = internalValue.value.filter((val: any) => {
    const valId = typeof val === 'object' ? val[computedItemValue.value] : val;
    return String(valId).toLowerCase() !== String(valToRemove).toLowerCase();
  });
};

const isAllSelected = computed(() => {
  if (filteredItems.value.length === 0) return false;
  return filteredItems.value.every(item => isItemSelected({ raw: item }));
});

const isSomeSelected = computed(() => {
  if (filteredItems.value.length === 0) return false;
  return filteredItems.value.some(item => isItemSelected({ raw: item }));
});

const toggleSelectAll = () => {
  if (!props.multiple) return;

  const currentSelected = Array.isArray(internalValue.value) ? [...internalValue.value] : [];
  const filteredValues = filteredItems.value.map((item: any) => item[computedItemValue.value]);

  if (isAllSelected.value) {
    // Deselect filtered
    internalValue.value = currentSelected.filter(val => !filteredValues.includes(val));
  } else {
    // Select filtered
    const newSelected = new Set([...currentSelected, ...filteredValues]);
    internalValue.value = Array.from(newSelected);
  }
};

const onMenuUpdate = (isOpen: boolean): void => {
  if (isOpen) {
    nextTick(() => { setTimeout(() => filterInputRef.value?.focus(), 150); });
  } else {
    searchQuery.value = ''; // Reset search on close
  }
};
</script>

<style scoped>
.entity-select-wrapper {
  width: 100%;
}

.entity-select :deep(.v-field) {
  background-color: rgba(var(--v-theme-surface), 0.5) !important;
  border-radius: 8px;
}

.sticky-search {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

:deep(.v-list-item) {
  padding-inline-start: 16px !important;
}

:deep(.v-list-item__prepend) {
  margin-inline-end: 4px !important;
  display: flex !important;
  align-items: center !important;
  overflow: visible !important;
}

:deep(.v-list-item__prepend > .v-icon),
:deep(.v-list-item__prepend > .v-avatar) {
  flex-shrink: 0 !important;
}

:deep(.v-chip) {
  overflow: visible !important;
}

:deep(.v-chip__prepend) {
  margin-inline-start: 2px !important;
}
</style>
